import { Reveal, Kicker } from "./Reveal";

const STEPS = [
  {
    n: "1",
    title: "Propose",
    desc: "Pricing models and AI generate a typed commercial Decision Proposal.",
    chips: ["Recommended action", "Expected effect", "Confidence", "Rationale", "Model version"],
  },
  {
    n: "2",
    title: "Govern",
    desc: "The Axiosera Policy Engine evaluates the proposal against a formally approved, versioned policy. The decision is deterministically classified:",
    chips: ["Allow autonomous", "Require approval", "Reject", "Block"],
  },
  {
    n: "3",
    title: "Execute",
    desc: "Approved actions reach the commerce system through controlled execution.",
    chips: ["Idempotent", "Observable", "Conflict-aware", "Reversible", "Circuit breakers"],
  },
  { n: "4", title: "Record", desc: "Every material event enters the Decision Ledger.", chips: [] },
  { n: "5", title: "Measure", desc: "The commercial result is compared with the predicted outcome and relevant control.", chips: [] },
];

const YAML = [
  ["policy", "uk-grocery-base-v14"],
  ["status", "active"],
  ["approved", "2026-06-12"],
  ["bounds", null],
  ["  price_delta_max", "4% per change"],
  ["  margin_floor", "22%"],
  ["  change_frequency", "1 per day per SKU"],
  ["approvals", null],
  ["  within_bounds", "autonomous (L3)"],
  ["  above_bounds", "pricing_manager"],
  ["autonomy_level", "L3"],
  ["audit", "full deterministic replay"],
];

const CLI = [
  ["$ axiosera policy simulate v15 --window 90d", "text-white"],
  ["replaying 41,203 ledger decisions ...", "text-zinc-500"],
  ["under v15:", "text-zinc-400"],
  ["  3,180 decisions change disposition", "text-zinc-400"],
  ["  est. margin effect +0.4pt", "text-[#00FF41]"],
  ["  new human routings +212/week", "text-zinc-400"],
  ["  bound violations 0", "text-[#00FF41]"],
  ["submit for approval? [y/N]", "text-white"],
];

export default function HowItWorks() {
  return (
    <section id="how" className="border-b border-white/10" data-testid="how-section">
      <div className="mx-auto max-w-[1600px] px-6 py-28 lg:px-12 lg:py-40">
        <Reveal>
          <Kicker index="05" label="How it works" />
          <h2 className="max-w-5xl font-display text-4xl font-extrabold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl" data-testid="how-heading">
            Models propose. <span className="text-[#00FF41]">Deterministic policy governs.</span>
          </h2>
        </Reveal>

        <div className="mt-20 grid gap-12 lg:grid-cols-12">
          <div className="space-y-0 border border-white/10 lg:col-span-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06} className="border-b border-white/10 p-8 transition-colors duration-500 last:border-b-0 hover:bg-[#0F0F11]">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-xs text-[#00FF41]">STEP {s.n}</span>
                  <h3 className="font-display text-xl font-bold tracking-tight" data-testid={`how-step-${s.n}`}>{s.title}</h3>
                </div>
                <p className="mt-3 font-mono text-xs leading-relaxed text-zinc-500">{s.desc}</p>
                {s.chips.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {s.chips.map((c) => (
                      <span key={c} className="border border-white/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                        {c}
                      </span>
                    ))}
                  </div>
                )}
              </Reveal>
            ))}
          </div>

          <div className="space-y-6 lg:col-span-6">
            <Reveal className="scanlines overflow-hidden border border-white/10 bg-[#0F0F11]">
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-3">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">policy.yaml · versioned object</span>
                <span className="flex gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-zinc-700" />
                  <span className="h-2 w-2 rounded-full bg-zinc-700" />
                  <span className="h-2 w-2 rounded-full bg-[#00FF41]" />
                </span>
              </div>
              <pre className="overflow-x-auto p-8 font-mono text-xs leading-loose" data-testid="policy-yaml-block">
                {YAML.map(([k, v], i) => (
                  <div key={i}>
                    <span className="text-zinc-300">{k}:</span>
                    {v && <span className="text-[#00FF41]"> {v}</span>}
                  </div>
                ))}
              </pre>
              <div className="border-t border-white/10 px-8 py-5 font-mono text-[11px] leading-relaxed text-zinc-500">
                Bounds are enforced by deterministic code — never by a model. Every decision references
                the exact policy version it acted under.
              </div>
            </Reveal>

            <Reveal delay={0.15} className="scanlines overflow-hidden border border-white/10 bg-[#0F0F11]">
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-3">
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">axiosera — policy simulate</span>
                <span className="font-mono text-[11px] text-[#00FF41]">REPLAY · 90D</span>
              </div>
              <pre className="p-8 font-mono text-xs leading-loose" data-testid="policy-cli-block">
                {CLI.map(([line, cls], i) => (
                  <div key={i} className={cls}>{line}</div>
                ))}
                <span className="blink text-white" />
              </pre>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
